import { useCallback } from 'react';
import {
  useCreateSharedPreset,
  useEvictSharedPreset,
  useUnshareSharedPreset,
  useUpdateSharedPreset,
} from './api/hooks';
import type { SharedPreset } from './api/types';

/** Что переносится между личным пресетом и совместным в обе стороны */
export type SharedPresetTransfer<TSettings> = {
  name: string;
  settings: TSettings;
  /**
   * Закрепление переносится: у совместного пресета оно своё у каждого участника, и владелец
   * сохраняет то, что было у его пресета
   */
  pinned: boolean;
};

type Params<TSettings> = {
  /** Таблица или дашборды, к которым относятся пресеты */
  tableName: string;
  /** Применённый пресет: применённость переходит вместе с ним */
  activePresetId: string | undefined;
  /** Идентификатор записи сервера по ключу совместного пресета; `null` — пресет не совместный */
  parseSharedId: (presetId: string) => number | null;
  /** Личный пресет, которым делятся */
  findPersonal: (presetId: string) => SharedPresetTransfer<TSettings> | undefined;
  /** Совместный пресет, возвращаемый в личные, — таким, каким владелец видел его, подтверждая окно */
  findShared: (presetId: string) => SharedPresetTransfer<TSettings> | undefined;
  /**
   * Заменить личный пресет созданным совместным: убрать личный и перенести применённость. Созданный
   * к этому времени уже есть в полученном списке
   */
  replaceWithShared: (personalId: string, created: SharedPreset<TSettings>, wasActive: boolean) => void;
  /**
   * Завести личный пресет на месте совместного, возвращённого в личные, и перенести применённость.
   * Совместный к этому времени уже убран из полученных списков
   */
  replaceWithPersonal: (sharedPresetId: string, preset: SharedPresetTransfer<TSettings>, wasActive: boolean) => void;
};

/**
 * «Поделиться» пресетом — один сценарий для пресетов таблицы и пользовательских дашбордов.
 *
 * Личный пресет становится совместным с теми же названием, содержимым и закреплением, у совместного,
 * которым пользователь владеет, меняются получатели, а без получателей совместный возвращается в личные.
 * Где лежит личный пресет и как на него переносится применённость, знает потребитель — это колбэки
 * `replaceWith*`. `onShared` вызывается по ответу сервера: отказ оставляет пресет как был, а о причине
 * сообщает общий перехватчик запросов.
 */
export const useSharePreset = <TSettings>({
  tableName,
  activePresetId,
  parseSharedId,
  findPersonal,
  findShared,
  replaceWithShared,
  replaceWithPersonal,
}: Params<TSettings>) => {
  const { mutateAsync: createSharedPreset, isPending: isCreating } = useCreateSharedPreset<TSettings>();
  // Смена получателей — своим экземпляром мутации: её ожидание и есть ожидание «Поделиться»,
  // переименование и запись содержимого в него не входят
  const { mutate: updateRecipients, isPending: isUpdatingRecipients } = useUpdateSharedPreset<TSettings>();
  const { mutateAsync: unsharePreset, isPending: isUnsharing } = useUnshareSharedPreset();
  const evictSharedPreset = useEvictSharedPreset();

  const unshare = useCallback(
    async (presetId: string, sharedId: number, onShared?: () => void) => {
      const preset = findShared(presetId);
      if (!preset) return;
      const wasActive = activePresetId === presetId;
      const isUnshared = await unsharePreset(sharedId).then(
        () => true,
        () => false,
      );
      if (!isUnshared) return;
      // Совместный уходит из списка в том же такте, в котором заводится личный: без обоих закреплённый
      // раздел пресета остался бы без своего адреса, а применённый снялся бы; с обоими разом меню
      // показало бы дубль
      evictSharedPreset(sharedId);
      replaceWithPersonal(presetId, preset, wasActive);
      onShared?.();
    },
    [unsharePreset, evictSharedPreset, findShared, replaceWithPersonal, activePresetId],
  );

  const share = useCallback(
    async (presetId: string, recipientIds: number[], onShared?: () => void) => {
      const sharedId = parseSharedId(presetId);
      if (sharedId !== null) {
        if (recipientIds.length === 0) {
          await unshare(presetId, sharedId, onShared);
          return;
        }
        updateRecipients({ id: sharedId, recipientIds }, { onSuccess: onShared });
        return;
      }
      const preset = findPersonal(presetId);
      if (!preset) return;
      const wasActive = activePresetId === presetId;
      const created = await createSharedPreset({
        tableName,
        name: preset.name,
        settings: preset.settings,
        recipientIds,
        isPinned: preset.pinned,
      }).catch(() => null);
      if (!created) return;
      // Перенос доводится здесь, а не колбэком мутации: ответ приходит уже с перечитанным списком,
      // где закреплённый раздел пресета переходит на новый ключ, — окно, из которого поделились,
      // к этому времени размонтировано, и колбэк не сработал бы, оставив личного двойника.
      replaceWithShared(presetId, created, wasActive);
      onShared?.();
    },
    [
      createSharedPreset,
      updateRecipients,
      unshare,
      parseSharedId,
      findPersonal,
      replaceWithShared,
      activePresetId,
      tableName,
    ],
  );

  return {
    share,
    /**
     * Запрос «Поделиться» — создание совместного пресета, смена его получателей или возврат в личные —
     * в работе
     */
    isSharing: isCreating || isUpdatingRecipients || isUnsharing,
  };
};
